<template>
  <div>
    <main class="container">
      <table-header
        title="Annual Access Review"
        :is-loading="isLoading"
        :is-empty="!AAR"
      >
        <template>
          <button
            v-if="AAR && AAR.active && !AAR.viewOnly"
            class="btn btn-primary-ford-orange white"
            @click="completeAAR"
          >
            <i
              v-if="isAARCompleteLoading"
              class="spinner-border spinner-btn mr-1"
            />
            Провести
          </button>
        </template>
      </table-header>
      <transition
        name="fade"
        mode="out-in"
      >
        <div
          v-if="AAR"
          class="aar-info"
        >
          <div>
            <span class="mr-2">AAR: <b>{{ AAR.key }}</b>,</span>
            <span class="mr-2">Пользователей: <b>{{ AAR.total }}</b>,</span>
            <span class="mr-2">статус: <b>{{ AAR.active ? 'Активен' : 'Завершен' }}</b></span>
          </div>
          <div class="d-flex">
            <span class="mr-2">Дата начала: <b>{{ AAR.startDate | parseDate }}</b>,</span>
            <span
              v-if="!AAR.active"
              class="mr-2"
            >
              Дата завершения: <b>{{ AAR.completionDate | parseDate }}</b>
            </span>
          </div>
          <div>
            Ответственные: <b>{{ responsible }}</b>
          </div>
        </div>
      </transition>

      <div class="table-wrapper">
        <div class="table-container">
          <transition
            name="fade"
            mode="out-in"
          >
            <table
              v-if="!isLoading && AAR && AAR.users.length"
              class="table"
            >
              <thead>
                <tr>
                  <th
                    v-for="header in tableHeaders"
                    :key="header.key"
                    :class="[{'sortable sortable_asc': header.sortable}, getSortClass(header.key)]"
                    @click="changeSortKey(header.key, header.sortable)"
                  >
                    <div class="table__header">
                      {{ header.label }}
                    </div>
                  </th>
                </tr>
              </thead>
              <tbody>
                <tr
                  v-for="(it, i) in AAR.users"
                  :key="it.id"
                >
                  <td>{{ i + 1 }}</td>
                  <td>{{ it.email }}</td>
                  <td>{{ it.role.join(', ') }}</td>
                  <td>
                    <div class="btn-group">
                      <button
                        class="btn btn-80 btn-sm"
                        :class="it.accessRequired === true ? 'btn-success' : 'btn-secondary'"
                        :disabled="it.disabled || !AAR.active || AAR.viewOnly"
                        @click="editAccess(it, true)"
                      >
                        ДА
                      </button>
                      <button
                        class="btn btn-80 btn-secondary btn-sm"
                        disabled
                      >
                        <i
                          v-if="it.isLoading"
                          class="spinner-border spinner-btn ml-1"
                        />
                      </button>
                      <button
                        class="btn btn-80 btn-sm"
                        :class="it.accessRequired === false ? 'btn-danger' : 'btn-secondary'"
                        :disabled="it.disabled || !AAR.active"
                        @click="editAccess(it, false)"
                      >
                        НЕТ
                      </button>
                    </div>
                  </td>
                </tr>
              </tbody>
            </table>
          </transition>
        </div>
      </div>
      <table-footer
        v-if="AAR && AAR.users.length"
        :current-page="currentPage"
        :per-page="perPage"
        :limit="limit"
        :total-count="totalCount"
        @pageChanged="page => (currentPage = page)"
        @limitChanged="lim => (limit = lim)"
      />
    </main>
  </div>
</template>

<script>
import { mapState } from 'vuex';
import TableHeader from '@/components/table/TableHeader';
import TableFooter from '@/components/table/TableFooter';

export default {
  name: 'AAR',
  components: {
    TableHeader,
    TableFooter,
  },
  props: {
    id: String,
  },
  data: () => ({
    isLoading: true,
    AAR: null,
    tableHeaders: [
      {
        key: 'id',
        label: '№',
      },
      {
        key: 'email',
        label: 'Email',
        sortable: true,
      },
      {
        key: 'role',
        label: 'Роли',
      },
      {
        key: 'accessRequired',
        label: 'Доступ подтвержден',
        sortable: true,
      },
    ],
    currentPage: 1,
    perPage: 10,
    limit: 10,
    totalCount: null,
    pageWatcherDisabled: false,
    isAARCompleteLoading: false,
    sortReversed: false,
    sortKey: '',
    sort: '',
  }),
  computed: {
    ...mapState(['user']),
    pages() {
      return Math.ceil(this.totalCount / this.perPage);
    },
    offset() {
      return this.limit * (this.currentPage - 1);
    },
    responsible() {
      return this.AAR.responsible.map(responsible => responsible.email).join(', ');
    },
  },
  watch: {
    limit() {
      this.pageWatcherDisabled = true;
      this.currentPage = 1;
      this.getAAR();
    },
    currentPage() {
      if (this.pageWatcherDisabled) {
        return;
      }

      this.getAAR();
    },
  },

  created() {
    this.getAAR();
  },
  methods: {
    async getAAR() {
      this.isLoading = true;
      const AAR = await this.$http
        .get(`users/1.0/aar/${this.id}`, {
          params: {
            limit: this.limit,
            skip: this.offset,
            sort: this.sort,
          },
        })
        .finally(() => {
          this.isLoading = false;
        });

      this.AAR = AAR;
      AAR.users = AAR.users.map(it => ({
        ...it,
        isLoading: false,
        disabled: it.aarKey && it.aarKey !== this.AAR.key,
      }));

      AAR.viewOnly = !AAR.responsible.find((item) => item.email === this.user.email);

      this.totalCount = AAR.total;
      this.perPage = this.limit;
      this.pageWatcherDisabled = false;
    },
    async editAccess({ email }, accessRequired) {
      const user = this.AAR.users.find(it => it.email === email);
      user.isLoading = true;
      try {
        await this.$http.post(`users/1.0/aar/${this.id}`, {
          email,
          accessRequired,
        });
        user.accessRequired = accessRequired;
      } finally {
        user.isLoading = false;
      }
    },
    async completeAAR() {
      this.isAARCompleteLoading = true;
      try {
        await this.$http.post(`users/1.0/aar-complete/${this.id}`);
        this.getAAR();
      } finally {
        this.isAARCompleteLoading = false;
      }
    },
    changeSortKey(sortKey, sortable) {
      if (this.isLoading || !sortable) {
        return;
      }

      this.sortReversed = this.sortKey === sortKey ? !this.sortReversed : false;
      this.sortKey = sortKey;

      this.sort = (this.sortReversed ? '-' : '+') + 'users~' + this.sortKey;
      this.pageWatcherDisabled = true;
      this.currentPage = 1;
      this.getAAR();
    },
    getSortClass(sortKey) {
      return { [`sortable_active ${this.sortReversed ? 'sortable_desc' : 'sortable_asc'}`]: this.sortKey === sortKey };
    },
  },
};
</script>

<style lang="scss" scoped>
.aar-info {
  margin-top: -20px;
}

td:first-child {
  min-width: 20px !important;
  width: 20px;
}

.btn-80 {
  width: 60px;
}

.btn.btn-success,
.btn.btn-success:hover {
  background-color: #4eca8c;
  border-color: #4eca8c;
  outline: none;
  color: white;
  z-index: 0;
}

.btn.btn-danger,
.btn.btn-danger:hover {
  background-color: #c70013;
  border-color: #c70013;
  outline: none;
  color: white;
  z-index: 0;
}

.btn-secondary {
  background-color: #6f6f6f;
  border-color: #6f6f6f;
  color: white;
}

.spinner-btn {
  border-color: #b3b2b2;
  border-right-color: transparent;
}
</style>
